import { useCallback, useState } from 'react';

import { DeviceScheduleContentType } from '@/enums';
import { Asset, SelectOptionProps } from '@/types';

enum SelectedAssetType {
  None = 'none',
  Upload = 'upload',
  Event = 'event',
  Organization = 'org'
}

type SelectedAsset =
  | { type: SelectedAssetType.None }
  | { type: SelectedAssetType.Upload; asset: Asset }
  | { type: SelectedAssetType.Event; eventId: string | number; assetId: number }
  | { type: SelectedAssetType.Organization; assetId: number };

type SelectionState = {
  source: SelectOptionProps | null;
  event: SelectOptionProps | null;
  asset: SelectedAsset;
};

export const useDeviceScheduleSelection = (
  deviceScheduleData: { contentType?: DeviceScheduleContentType; asset?: Asset | null } | undefined,
  sourceOptions: SelectOptionProps[],
  eventOptions: SelectOptionProps[]
) => {
  const [state, setState] = useState<SelectionState>({
    source: null,
    event: null,
    asset: { type: SelectedAssetType.None }
  });

  const setSource = useCallback((source: SelectOptionProps | null) => {
    setState((prev) => ({ ...prev, source }));
  }, []);

  const setEvent = useCallback((event: SelectOptionProps | null) => {
    setState((prev) => ({ ...prev, event }));
  }, []);

  const createDeviceScheduleSelection = useCallback(
    (asset: Asset | null | undefined, contentType?: DeviceScheduleContentType) => {
      const source = sourceOptions.find((s) => s.value === contentType) ?? null;

      if (contentType === DeviceScheduleContentType.NewAsset && asset) {
        return {
          source,
          event: null,
          selection: { type: SelectedAssetType.Upload, asset } as SelectedAsset
        };
      }

      if (contentType === DeviceScheduleContentType.EventAsset && asset?.eventId !== undefined) {
        const event =
          eventOptions.find((option) => String(option.value) === String(asset.eventId)) ?? null;

        return {
          source,
          event,
          selection: {
            type: SelectedAssetType.Event,
            eventId: asset.eventId,
            assetId: asset.id
          } as SelectedAsset
        };
      }

      if (contentType === DeviceScheduleContentType.OrganizationAsset && asset?.id) {
        return {
          source,
          event: null,
          selection: { type: SelectedAssetType.Organization, assetId: asset.id } as SelectedAsset
        };
      }

      return {
        source,
        event: null,
        selection: { type: SelectedAssetType.None } as SelectedAsset
      };
    },
    [eventOptions, sourceOptions]
  );

  const resetSelectionFromSchedule = useCallback(() => {
    const isPushContentType =
      deviceScheduleData?.contentType === DeviceScheduleContentType.NewAsset ||
      deviceScheduleData?.contentType === DeviceScheduleContentType.EventAsset ||
      deviceScheduleData?.contentType === DeviceScheduleContentType.OrganizationAsset;

    const contentType = isPushContentType
      ? deviceScheduleData?.contentType
      : DeviceScheduleContentType.OrganizationAsset;

    const { selection, source, event } = createDeviceScheduleSelection(
      deviceScheduleData?.asset,
      contentType
    );

    setState({
      source,
      event,
      asset: selection
    });
  }, [createDeviceScheduleSelection, deviceScheduleData?.asset, deviceScheduleData?.contentType]);

  const assignAsset = useCallback(
    (asset: Asset, contentType: DeviceScheduleContentType) => {
      const { selection, event, source } = createDeviceScheduleSelection(asset, contentType);

      setState((prev) => ({
        ...prev,
        source: source ?? prev.source,
        event: event ?? prev.event,
        asset: selection
      }));
    },
    [createDeviceScheduleSelection]
  );

  const selectedEventAssetId = (() => {
    if (state.asset.type !== SelectedAssetType.Event || !state.event) {
      return null;
    }

    return String(state.event.value) === String(state.asset.eventId) ? state.asset.assetId : null;
  })();

  const selectedOrganizationAssetId =
    state.asset.type === SelectedAssetType.Organization ? state.asset.assetId : null;

  const uploadedAsset = state.asset.type === SelectedAssetType.Upload ? state.asset.asset : null;

  return {
    selectedSource: state.source,
    selectedEvent: state.event,
    selectedEventAssetId,
    selectedOrganizationAssetId,
    uploadedAsset,
    setSource,
    setEvent,
    assignAsset,
    resetSelectionFromSchedule
  };
};
